'use client'

import React, { useState, useEffect, useCallback, useRef } from 'react'
import clsx from 'clsx'
import useEmblaCarousel from 'embla-carousel-react'
import type { EmblaOptionsType } from 'embla-carousel'
import ArticleCard from '@/components/Shared/Card/ArticleCard'
import BookCard from '@/components/Shared/Card/BookCard'
import MovieCard from '@/components/Shared/Card/MovieCard'
import CompanySquareCard from '@/components/Shared/Card/CompanySquareCard'
import CompanyGroupCard from '@/components/Shared/Card/CompanyGroupCard'
import RecruitCard from '@/components/Shared/Card/RecruitCard'
import BannerContent from '@/components/Shared/BannerContent'
import type { JoinedGroup } from '@/types/group'
import type { CarouselContentsProps } from '@/types/carousel'
import type { Story } from '@/types/story'
import styles from './styles.module.scss'

export default function CarouselContents({
  items,
  type,
  used = '',
  display = '',
  recommend = false,
  bookId = null,
  articleTitle,
}: CarouselContentsProps) {
  const isTopCarousel = used === 'top-carousel'
  const isCompany = used === 'company'
  const isFull = display === 'full'
  const mainUsed = (() => {
    if (isFull) return 'carousel'
    if (isTopCarousel) return 'top-carousel'
    if (isCompany) return 'company'
    return 'carousel'
  })()

  const stableIdBase = `${type}-${used || 'default'}-${display || 'default'}-${items.length}`
  const uniqueId = useRef(`embla-${stableIdBase}`)

  const emblaOptions: EmblaOptionsType = {
    loop: isTopCarousel,
    align: isTopCarousel ? 'center' : 'start',
    slidesToScroll: 1,
    dragFree: false,
    containScroll: 'trimSnaps',
    duration: 20,
    skipSnaps: false,
    breakpoints: {
      '(max-width: 767px)': {
        // SP
        slidesToScroll: 1,
        align: 'start',
      },
      '(min-width: 768px)': {
        // PC
        slidesToScroll: 1,
      },
    },
  }

  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions)
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false)
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)

  // display="fixed"でない場合にボタンを表示
  const shouldShowButtons = display !== 'fixed'

  // クラス名を組み立てるヘルパー関数
  const getCarouselWrapperClass = () => {
    const classes = [styles.carouselWrapper]

    // used prop に応じてusedと同名のclassを追加
    if (used && styles[used]) {
      classes.push(styles[used])
    }

    // display prop に応じてdisplayと同名のclassを追加
    if (display && styles[display]) {
      classes.push(styles[display])
    }

    // type prop に応じてtypeと同名のclassを追加
    if (type && styles[type]) {
      classes.push(styles[type])
    }

    return classes.join(' ')
  }

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi])
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi])

  const handleSlideClick = () => {
    // Disable slide transition temporarily
    if (emblaApi) {
      emblaApi.reInit({ duration: 0 })
      setTimeout(() => {
        emblaApi.reInit({ duration: 25 })
      }, 0)
    }
  }

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setPrevBtnEnabled(emblaApi.canScrollPrev())
    setNextBtnEnabled(emblaApi.canScrollNext())
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return

    onSelect()
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)

    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('reInit', onSelect)
    }
  }, [emblaApi, onSelect])

  // Touch event handling
  useEffect(() => {
    if (!emblaApi) return

    const handleTouchEvent = (event: TouchEvent) => {
      event.stopPropagation()
    }

    const emblaNode = emblaApi.rootNode()
    if (emblaNode) {
      emblaNode.addEventListener('touchstart', handleTouchEvent)
      emblaNode.addEventListener('touchmove', handleTouchEvent)
      emblaNode.addEventListener('touchend', handleTouchEvent)

      return () => {
        emblaNode.removeEventListener('touchstart', handleTouchEvent)
        emblaNode.removeEventListener('touchmove', handleTouchEvent)
        emblaNode.removeEventListener('touchend', handleTouchEvent)
      }
    }
  }, [emblaApi])

  return (
    <div className={getCarouselWrapperClass()} id={uniqueId.current}>
      <div className={styles.embla} ref={emblaRef}>
        <div className={styles.emblaContainer}>
          {items.map((item, index) => (
            <div
              key={item.id || index}
              className={clsx(styles.emblaSlide, {
                [styles.carouselBanner]: type === 'banner',
                [styles[type]]: type,
              })}
            >
              {type === 'article' && (
                <ArticleCard
                  article={item as Story}
                  index={index}
                  used={mainUsed}
                  extraUsed={isCompany ? 'company' : ''}
                  recommend={recommend}
                  onSlideClick={handleSlideClick}
                  bookId={bookId}
                />
              )}
              {type === 'banner' && <BannerContent banner={item as any} />}
              {type === 'squareCompany' && <CompanySquareCard company={item as any} />}
              {type === 'book' && <BookCard book={item as any} />}
              {type === 'movie' && <MovieCard movie={item as any} used={mainUsed} />}
              {type === 'recruit' && (
                <RecruitCard recruit={item as any} used='carousel' articleTitle={articleTitle} />
              )}
              {type === 'group' && <CompanyGroupCard group={item as JoinedGroup} used={mainUsed} />}
            </div>
          ))}
        </div>
      </div>

      {shouldShowButtons && (
        <>
          <button
            className={clsx(
              styles.swiperButtonPrev,
              styles.swiperButton,
              `${uniqueId.current}-prev`,
              { [styles.swiperButtonDisabled]: !prevBtnEnabled },
            )}
            onClick={scrollPrev}
            disabled={!prevBtnEnabled}
            aria-label='Previous'
            data-carousel-id={uniqueId.current}
          />

          <button
            className={clsx(
              styles.swiperButtonNext,
              styles.swiperButton,
              `${uniqueId.current}-next`,
              { [styles.swiperButtonDisabled]: !nextBtnEnabled },
            )}
            onClick={scrollNext}
            disabled={!nextBtnEnabled}
            aria-label='Next'
            data-carousel-id={uniqueId.current}
          />
        </>
      )}

      {isTopCarousel && (
        <div className={styles.swiperPagination}>
          {items.map((_, index) => (
            <button
              key={index}
              className={clsx(styles.paginationBullet, {
                [styles.paginationBulletActive]: selectedIndex === index,
              })}
              onClick={() => emblaApi?.scrollTo(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
